import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import searchIcon from "../../../assets/images/icons/search-white.svg";
import styles from "./ClassEventFilters.module.scss";
import { Form, Select, Divider, Slider, Checkbox } from "antd";
import { EClassLevels, IClassCategory, IClassEventTimeRange } from "../../../models/ClassEvent";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ISettings } from "../../../models/Settings";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Option } = Select;

interface IClassEventsFilters {
  credits: [number, number];
  selectedTimes: IClassEventTimeRange[];
  selectedDate: dayjs.Dayjs | null;
  selectedLevels: string[];
  settings: ISettings | null;
  allCategories: IClassCategory[];
  categories: string[];
  timeRangeOptions: IClassEventTimeRange[];
  onSearch: () => void;
  onResetAll: () => void;
  onSelectDate: (date: dayjs.Dayjs) => void;
  onSelectTimes: (times: IClassEventTimeRange[]) => void;
  onCreditsChange: (credits: [number, number]) => void;
  onToggleLevel: (level: string) => void;
  onSelectCategories: (categories: string[]) => void;
}

const ClassEventsFilters: React.FC<IClassEventsFilters> = ({
  credits,
  selectedTimes,
  selectedDate,
  selectedLevels,
  settings,
  allCategories,
  categories,
  timeRangeOptions,
  onSearch,
  onResetAll,
  onSelectDate,
  onSelectTimes,
  onCreditsChange,
  onToggleLevel,
  onSelectCategories,
}) => {
  const { t } = useTranslation();

  const [draftTimes, setDraftTimes] = useState<IClassEventTimeRange[]>(selectedTimes);
  const [draftCredits, setDraftCredits] = useState<[number, number]>(credits);
  const [draftLevels, setDraftLevels] = useState<string[]>(selectedLevels);

  const [openTime, setOpenTime] = useState(false);
  const [openMore, setOpenMore] = useState(false);

  const levelOptions = useMemo(() => Object.values(EClassLevels), []);
  const formatLocalTime = (isoUtc: string) => dayjs.utc(isoUtc).tz(dayjs.tz.guess()).format("h a");

  const toggleTime = (time: IClassEventTimeRange) => {
    setDraftTimes((prev) => {
      const exists = prev.some((t) => t.id === time.id);
      return exists ? prev.filter((t) => t.id !== time.id) : [...prev, time];
    });
  };

  useEffect(() => {
    setDraftTimes(selectedTimes);
    setDraftCredits(credits);
    setDraftLevels(selectedLevels);
  }, [selectedTimes, credits, selectedLevels]);

  return (
    <div className={styles.headerBox}>
      <Form.Item
        className={`form-item__select horizontal ${styles.selectWrapper}`}
        label={t("Categories")}
      >
        <Select
          mode="multiple"
          className="select"
          variant="borderless"
          optionFilterProp="children"
          showSearch
          placeholder={t("All")}
          value={categories}
          onChange={onSelectCategories}
        >
          {allCategories.map((el) => (
            <Option value={el.id} key={el.id}>
              {el.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        className={`form-item__select horizontal ${styles.selectWrapper}`}
        label={t("Date")}
      >
        <Select
          className="select select--date"
          style={{ width: 220 }}
          placeholder={t("Select date")}
          value={selectedDate ? selectedDate.format("ddd, MMM D") : undefined}
          onChange={(value) => onSelectDate(dayjs(value))}
        >
          {(() => {
            if (!settings?.classVisibilityWeeks) return null;

            const today = dayjs().startOf("day");
            const maxDate = today.add(settings.classVisibilityWeeks * 7, "day");
            const options = [];

            for (let i = 0; i <= maxDate.diff(today, "day"); i++) {
              const date = today.add(i, "day");
              options.push(
                <Option key={date.toISOString()} value={date.toISOString()}>
                  {date.format("ddd, MMM D")}
                </Option>
              );
            }

            return options;
          })()}
        </Select>
      </Form.Item>

      <Form.Item
        className={`form-item__select horizontal ${styles.selectWrapper}`}
        label={t("Time")}
      >
        <Select
          style={{ width: 220 }}
          open={openTime}
          onDropdownVisibleChange={(visible) => {
            setOpenTime(visible);
            if (visible) setDraftTimes(selectedTimes);
          }}
          className="select select--time"
          variant="borderless"
          dropdownClassName="select-time-dropdown"
          getPopupContainer={() => document.body}
          dropdownRender={() => (
            <div style={{ padding: 12, width: 320 }}>
              <div className={styles.timeGrid}>
                {timeRangeOptions.map((el) => {
                  const isSelected = draftTimes.some((t) => t.id === el.id);

                  return (
                    <div
                      key={el.id}
                      className={`${styles.timeCard} ${isSelected ? styles.selected : ""}`}
                      onClick={() => toggleTime(el)}
                    >
                      <div className={styles.timeTitle}>{t(el.id.replace("-", " "))}</div>
                      <div className={styles.timeRange}>
                        {formatLocalTime(el.from)} - {formatLocalTime(el.to)}
                      </div>
                    </div>
                  );
                })}
              </div>

              <Divider style={{ margin: "8px 0" }} />

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span
                  className={styles.clearBtn}
                  onClick={() => {
                    onSelectTimes([]);
                    setOpenTime(false);
                  }}
                >
                  {t("Clear")}
                </span>
                <CustomButton
                  text={t("Done")}
                  type="button"
                  typeStyle="fill"
                  functionOnClick={() => {
                    onSelectTimes(draftTimes);
                    setOpenTime(false);
                  }}
                  style={{ width: 80, height: 30 }}
                />
              </div>
            </div>
          )}
          value={
            selectedTimes.length
              ? selectedTimes
                  .map((tItem) => `${formatLocalTime(tItem.from)} – ${formatLocalTime(tItem.to)}`)
                  .join(", ")
              : t("All")
          }
        />
      </Form.Item>

      <Form.Item
        className={`form-item__select horizontal ${styles.selectWrapper}`}
        label={t("More filters")}
      >
        <Select
          style={{ width: 220 }}
          open={openMore}
          onDropdownVisibleChange={(visible) => {
            setOpenMore(visible);
            if (visible) {
              setDraftCredits(credits);
              setDraftLevels(selectedLevels);
            }
          }}
          className="select select--more"
          variant="borderless"
          dropdownClassName="select-more-dropdown"
          getPopupContainer={() => document.body}
          dropdownRender={() => (
            <div style={{ padding: 12, width: 320 }}>
              {/* Credits */}
              <div className={styles.moreBlock}>
                <div className={styles.moreLabel}>
                  {t("Credits").split("r")[0].toUpperCase() + t("Credits").slice(1)}
                </div>
                <div className={styles.sliderHeader}>
                  {credits[0]} - {credits[1]}
                </div>
                <Slider
                  range
                  min={0}
                  max={20}
                  step={1}
                  value={draftCredits}
                  styles={{
                    rail: {
                      backgroundColor: "#E2E8F0",
                      height: 6,
                    },
                    track: {
                      backgroundColor: "#FF8C42",
                      height: 6,
                    },
                    handle: {
                      borderColor: "#FF8C42 !important",
                    },
                  }}
                  onChange={(val) => setDraftCredits(val as [number, number])}
                />
              </div>

              <Divider style={{ margin: "8px 0" }} />

              <div className={styles.moreBlock}>
                <div className={styles.moreLabel}>{t("Levels")}</div>
                <div className={styles.levelsGrid}>
                  {levelOptions.map((lvl, index) => (
                    <Checkbox
                      key={lvl}
                      checked={draftLevels.includes(lvl)}
                      onChange={() => {
                        setDraftLevels((prev) =>
                          prev.includes(lvl) ? prev.filter((x) => x !== lvl) : [...prev, lvl]
                        );
                      }}
                      style={{ marginLeft: index === 1 ? 6 : 0 }}
                    >
                      {t(lvl)}
                    </Checkbox>
                  ))}
                </div>
              </div>

              <Divider style={{ margin: "8px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span
                  className={styles.clearBtn}
                  onClick={() => {
                    setDraftCredits([0, 20]);
                    setDraftLevels([]);
                  }}
                >
                  {t("Clear")}
                </span>
                <CustomButton
                  text={t("Done")}
                  type="button"
                  typeStyle="fill"
                  functionOnClick={() => {
                    onCreditsChange(draftCredits);
                    draftLevels.forEach((lvl) => {
                      if (!selectedLevels.includes(lvl)) onToggleLevel(lvl);
                    });
                    selectedLevels.forEach((lvl) => {
                      if (!draftLevels.includes(lvl)) onToggleLevel(lvl);
                    });
                    setOpenMore(false);
                  }}
                  style={{ width: 80, height: 30 }}
                />
              </div>
            </div>
          )}
          value={
            draftLevels.length || draftCredits[0] !== 0 || draftCredits[1] !== 20
              ? `${draftCredits[0]}–${draftCredits[1]} ${t("Credits")}, ${draftLevels.join(", ")}`
              : t("Select...")
          }
        />
      </Form.Item>

      <div className={styles.btnWrapper}>
        <CustomButton
          text=""
          type="button"
          typeStyle="fill"
          icon={searchIcon}
          altIcon="search"
          functionOnClick={onSearch}
        />
        <span className={styles.reset} onClick={onResetAll}>
          {t("Reset")}
        </span>
      </div>
    </div>
  );
};

export default ClassEventsFilters;
